<template>
    <div class="flex justify-center">
        <div class="w-full md:w-11/12">
            <div v-if="loadingEarningsSync && !earningsSyncLoaded" class="mb-8 flex items-center py-4">
                <i class="pi pi-spin pi-spinner text-2xl" style="color: var(--color-primary)"></i>
                <span class="ml-3">Loading earnings sync...</span>
            </div>
            <div v-else-if="earningsSyncAllowed" class="mb-8">
                <h2 class="mb-2">Earnings sync</h2>
                <p class="mb-4 text-sm opacity-80">
                    Re-runs the earnings jobs now instead of waiting for the next scheduled pass — use it
                    after fixing a deal or mapping a campaign. Figures for the days covered are recomputed
                    from the current deals; manual adjustments are never overwritten.
                </p>
                <div class="flex flex-wrap gap-6">
                    <div v-for="kind in earningsSyncKinds" :key="kind.key" class="flex flex-col gap-1">
                        <Button
                            :label="kind.label"
                            icon="pi pi-refresh"
                            severity="primary"
                            outlined
                            :loading="triggering === kind.key"
                            :disabled="Boolean(triggering) || kind.inCooldown"
                            @click="confirmEarningsSync($event, kind)"
                        />
                        <small class="opacity-70">{{ kind.scope }}</small>
                        <small v-if="kind.inCooldown" class="opacity-70">
                            Just ran — again after {{ formatTime(kind.cooldownUntil) }}
                        </small>
                        <small v-else-if="kind.lastTriggeredAt" class="opacity-70">
                            Last run {{ formatWhen(kind.lastTriggeredAt) }}
                        </small>
                        <small v-else class="opacity-70">Not run from here yet</small>
                    </div>
                </div>
            </div>
            <div class="mb-8">
                <h2 class="mb-4">Automapped Campaigns</h2>
                <div v-if="loadingAutomapped" class="flex items-center justify-center py-10">
                    <i class="pi pi-spin pi-spinner text-5xl" style="color: var(--color-primary)"></i>
                    <span class="ml-4">Loading automapped campaigns...</span>
                </div>
                <div v-else-if="errorAutomapped" class="error">{{ errorAutomapped }}</div>
                <DataTable
                    v-else-if="automappedCampaigns.length"
                    :value="automappedCampaigns"
                    dataKey="_id"
                    scrollable
                    :scrollHeight="'50vh'"
                >
                    <Column field="userName" header="Publisher" :headerStyle="headerStyles" />
                    <Column field="siteBrandLink.market" header="Market" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            <div class="flex items-center gap-2">
                                <span v-if="data.siteBrandLink?.market" :class="`fi fi-${data.siteBrandLink.market.toLowerCase()}`" class="ml-1"></span>
                            </div>
                        </template>
                    </Column>

                    <Column field="siteBrandLink.advertiserName" header="Advertiser" :headerStyle="headerStyles" />
                    <Column field="voonixCampaignKey" header="Campaign Key" :headerStyle="headerStyles" />
                    <Column field="voonixCampaignName" header="Campaign Name" :headerStyle="headerStyles" />
                    <Column field="siteBrandLink.internalNote" header="Internal Note" :headerStyle="headerStyles" />
                    <Column field="earnings.clicks" header="Clicks" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ data.earnings?.clicks ?? 0 }}
                        </template>
                    </Column>
                    <Column field="earnings.ftds" header="FTDs" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ data.earnings?.ftds ?? 0 }}
                        </template>
                    </Column>
                    <Column field="earnings.revenue" header="Revenue" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ data.earnings?.revenue ?? 0 }}
                        </template>
                    </Column>
                    <Column field="siteBrandLink.updatedAt" header="Updated At" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ data.siteBrandLink?.updatedAt ? new Date(data.siteBrandLink.updatedAt).toLocaleString() : '-' }}
                        </template>
                    </Column>
                </DataTable>
                <div v-else class="text-center py-10">No automapped campaigns found.</div>
            </div>
            <div class="md:flex md:space-x-6">
                <div class="md:w-1/2">
                    <div v-if="loading" class="flex items-center justify-center py-10">
                        <i class="pi pi-spin pi-spinner text-5xl" style="color: var(--color-primary)"></i>
                        <span class="ml-4">Loading unmapped site links...</span>
                    </div>
                    <div v-else-if="error" class="error">{{ error }}</div>

                    <DataTable
                        v-else-if="unmappedSiteLinks.length"
                        :value="unmappedSiteLinks"
                        dataKey="_id"
                        scrollable
                        :scrollHeight="'75vh'"
                        @row-click="onRowClick"
                        :rowClass="getSelectedSiteLinkRowClass"
                    >
                        <Column header="UserName" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.userId?.name || '-' }}
                            </template>
                        </Column>
                        <Column field="voonixAdvertiserId.name" header="Advertiser" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.voonixAdvertiserId?.name || '-' }}
                            </template>
                        </Column>
                        <Column field="internalNote" header="Internal Note" :headerStyle="headerStyles" />
                        <Column field="market" header="Market" :headerStyle="headerStyles" />
                        <Column field="affiliateLink" header="Affiliate Link" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                <a v-if="data.affiliateLink" :href="data.affiliateLink" target="_blank" class="link-class">
                                    {{ data.affiliateLink }}
                                </a>
                                <span v-else>-</span>
                            </template>
                        </Column>
                        <Column field="createdAt" header="Created At" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.createdAt ? new Date(data.createdAt).toLocaleString() : '-' }}
                            </template>
                        </Column>
                    </DataTable>

                    <div v-else class="text-center py-10">No unmapped site links found.</div>
                </div>

                <div class="mt-6 md:mt-0 md:w-1/2">
                    <h2 class="mb-2">
                        Campaigns
                        <span v-if="selectedAdvertiserName"> for {{ selectedAdvertiserName }}</span>
                    </h2>
                    <DataTable
                        :value="selectedSiteLinkDetails"
                        dataKey="_id"
                        scrollable
                        :loading="loadingVoonixCampaigns"
                        :scrollHeight="'75vh'"
                        :rowClass="getRowClass"
                        @row-click="onCampaignRowClick"
                    >
                        <Column field="voonixCampaignKey" header="Key" :headerStyle="headerStyles" />
                        <Column field="voonixCampaignName" header="Name" :headerStyle="headerStyles" />
                        <Column field="earnings.clicks" header="Clicks" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.earnings?.clicks ?? 0 }}
                            </template>
                        </Column>
                        <Column field="earnings.ftds" header="FTDs" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.earnings?.ftds ?? 0 }}
                            </template>
                        </Column>
                        <Column field="earnings.revenue" header="Revenue" :headerStyle="headerStyles">
                            <template #body="{ data }">
                                {{ data.earnings?.revenue ?? 0 }}
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>
        <ConfirmPopup />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import SiteLinkService from '@/services/SiteLinkService';
import CampaignService from '@/services/CampaignService';
import AdminService from '@/services/AdminService';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ConfirmPopup from 'primevue/confirmpopup';
import { headerStyles } from '@/global/headerStyles';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { campaignKeyMatch } from '@/utils/campaign-keys';

const unmappedSiteLinks = ref([]);
const unmappedCampaigns = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedSiteLinkDetails = ref([]);
const selectedSiteLink = ref(null);
const selectedAdvertiserName = ref('');
const loadingVoonixCampaigns = ref(true);
const selectedCampaign = ref(null);
const automappedCampaigns = ref([]);
const loadingAutomapped = ref(false);
const errorAutomapped = ref(null);
const confirm = useConfirm();
const toast = useToast();

// Earnings sync. The server decides whether this account may use it at all, and
// returns the day range and cooldown per kind, so none of that is duplicated here.
const EARNINGS_SYNC_ORDER = ['daily', 'month', 'lastMonth'];
const EARNINGS_SYNC_PROMPTS = {
    daily: 'Re-run the earnings sync for today?',
    month: 'Re-run the earnings sync for every day of this month up to yesterday?',
    lastMonth: 'Re-run the earnings sync for every day of last month?'
};
const loadingEarningsSync = ref(true);
const earningsSyncLoaded = ref(false);
const earningsSyncAllowed = ref(false);
const earningsSyncState = ref({});
const triggering = ref(null);
// The server's inCooldown is a snapshot. Without a clock the button would stay
// disabled past the end of the cooldown until something refetched the status.
const nowMs = ref(Date.now());
let cooldownTicker = null;

const earningsSyncKinds = computed(() => EARNINGS_SYNC_ORDER
    .filter((key) => earningsSyncState.value[key])
    .map((key) => {
        const kind = earningsSyncState.value[key];
        const until = kind.cooldownUntil ? new Date(kind.cooldownUntil).getTime() : null;
        return {
            key,
            ...kind,
            inCooldown: until ? until > nowMs.value : Boolean(kind.inCooldown)
        };
    }));

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : '');
const formatTime = (value) => (value
    ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '');

const fetchEarningsSyncStatus = async () => {
    loadingEarningsSync.value = true;
    try {
        const { data } = await AdminService.getEarningsSyncStatus();
        earningsSyncAllowed.value = Boolean(data?.allowed);
        earningsSyncState.value = data?.kinds || {};
        earningsSyncLoaded.value = true;
    } catch (e) {
        // Not being allowed to sync is not an error worth a toast on this page.
        // A LATER refetch that fails is different: the card is already on screen
        // and belongs to this account, so keep what we have rather than making it
        // disappear over a network blip.
        if (!earningsSyncLoaded.value) {
            earningsSyncAllowed.value = false;
            earningsSyncState.value = {};
        }
    } finally {
        loadingEarningsSync.value = false;
    }
};

const runEarningsSync = async (kind) => {
    triggering.value = kind.key;
    try {
        const { data } = await AdminService.triggerEarningsSync(kind.key);
        toast.add({
            severity: 'success',
            summary: 'Earnings sync started',
            detail: `${kind.label} — ${data?.scope || kind.scope}. It runs in the background; figures update as it goes.`,
            life: 5000
        });
    } catch (e) {
        const cooldown = e?.response?.status === 429;
        toast.add({
            severity: cooldown ? 'warn' : 'error',
            summary: cooldown ? 'Ran too recently' : 'Could not start the sync',
            detail: e?.response?.data?.error || e.message || 'Unknown error',
            life: 6000
        });
    } finally {
        triggering.value = null;
        await fetchEarningsSyncStatus();
    }
};

const confirmEarningsSync = (event, kind) => {
    confirm.require({
        target: event?.currentTarget || event?.target,
        message: `${EARNINGS_SYNC_PROMPTS[kind.key]} (${kind.scope})`,
        icon: 'pi pi-exclamation-triangle',
        rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
        acceptProps: { label: 'Run sync', severity: 'primary' },
        accept: () => runEarningsSync(kind)
    });
};

const fetchUnmappedSiteLinks = async () => {
    loading.value = true;
    error.value = null;
    try {
        const { data } = await SiteLinkService.getUnmappedForAutomapping();
        if (Array.isArray(data)) {
            unmappedSiteLinks.value = data.slice().sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );
        } else {
            unmappedSiteLinks.value = [];
        }
    } catch (e) {
        error.value = e?.response?.data?.message || e.message || 'Failed to load unmapped site links';
    } finally {
        loading.value = false;
    }
};

const fetchUnmappedCampaigns = async () => {
    loadingVoonixCampaigns.value = true;
    error.value = null;
    try {
        const { data } = await CampaignService.getUnmapped();
        unmappedCampaigns.value = Array.isArray(data) ? data : [];
    } catch (e) {
        error.value = e?.response?.data?.message || e.message || 'Failed to load unmapped campaigns';
    } finally {
        loadingVoonixCampaigns.value = false;
    }
};

const onRowClick = (event) => {
    selectedSiteLink.value = event?.data;
    const advertiserName = event?.data?.voonixAdvertiserId?.name;
    const advertiserVoonixId = event?.data?.voonixAdvertiserId?.voonixId;

    if (!advertiserName || !advertiserVoonixId) {
        selectedSiteLinkDetails.value = [];
        selectedAdvertiserName.value = '';
        return;
    }

    const match = unmappedCampaigns.value.find(
        (item) => item.advertiserName === advertiserName
    );

    if (!match || !Array.isArray(match.campaigns)) {
        selectedSiteLinkDetails.value = [];
        selectedAdvertiserName.value = '';
        return;
    }

    selectedSiteLinkDetails.value = match.campaigns.filter(
        (campaign) => String(campaign.voonixId) === String(advertiserVoonixId)
    );

    selectedAdvertiserName.value = advertiserName;
};

const getSelectedSiteLinkRowClass = (siteLink) => {
    if (selectedSiteLink.value?._id && siteLink?._id) {
        if (String(selectedSiteLink.value._id) === String(siteLink._id)) {
            return 'selected-site-link-row';
        }
    }
    return '';
};

// The campaign the selected link belongs to: by internalNote for most affiliate
// systems, by the `pid` inside the link for NetRefer — utils/campaign-keys.js.
const getRowClass = (campaign) => {
    if (selectedSiteLink.value && campaign?.voonixCampaignKey) {
        if (campaignKeyMatch(selectedSiteLink.value, campaign.voonixCampaignKey)) {
            return 'highlighted-campaign-row';
        }
    }
    return '';
};

const onCampaignRowClick = (event) => {
    selectedCampaign.value = event?.data;
    
    if (!selectedCampaign.value || !selectedSiteLink.value) {
        return;
    }

    confirm.require({
        target: event.originalEvent?.currentTarget || event.originalEvent?.target,
        message: `Do you want to map campaign "${selectedCampaign.value.voonixCampaignKey}" with "${selectedSiteLink.value.internalNote}" to this site link?`,
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Map',
            severity: 'primary'
        },
        accept: async () => {
            try {
                await SiteLinkService.mapCampaignToSiteBrandLink(
                    selectedCampaign.value._id,
                    selectedSiteLink.value._id
                );
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Campaign mapped successfully',
                    life: 3000
                });
                selectedCampaign.value = null;
                selectedSiteLink.value = null;
                selectedSiteLinkDetails.value = [];
                selectedAdvertiserName.value = '';
                // Refresh the data
                await Promise.all([
                    fetchUnmappedSiteLinks(),
                    fetchUnmappedCampaigns()
                ]);
            } catch (error) {
                console.error('Error mapping campaign:', error);
                toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error?.response?.data?.message || 'Failed to map campaign',
                    life: 3000
                });
            }
        },
        reject: () => {
            // User cancelled
        }
    });
};

const fetchAutomappedCampaigns = async () => {
    loadingAutomapped.value = true;
    errorAutomapped.value = null;
    try {
        const { data } = await CampaignService.getAutomapped();
        automappedCampaigns.value = Array.isArray(data) ? data : [];
    } catch (e) {
        errorAutomapped.value = e?.response?.data?.message || e.message || 'Failed to load automapped campaigns';
    } finally {
        loadingAutomapped.value = false;
    }
};

onMounted(async () => {
    cooldownTicker = setInterval(() => { nowMs.value = Date.now(); }, 15000);
    await Promise.all([
        fetchEarningsSyncStatus(),
        fetchUnmappedSiteLinks(),
        fetchUnmappedCampaigns(),
        fetchAutomappedCampaigns()
    ]);
});

onUnmounted(() => {
    if (cooldownTicker) clearInterval(cooldownTicker);
});
</script>

<style scoped>
.error {
    color: red;
}

:deep(.selected-site-link-row) {
    background-color: rgba(10, 45, 169, 0.2) !important;
    border-left: 3px solid rgb(34, 197, 94) !important;
}

:deep(.selected-site-link-row:hover) {
    background-color: rgba(2, 42, 176, 0.3) !important;
}

:deep(.highlighted-campaign-row) {
    background-color: rgba(59, 130, 246, 0.3) !important;
    border-left: 3px solid rgb(59, 130, 246) !important;
}

:deep(.highlighted-campaign-row:hover) {
    background-color: rgba(59, 130, 246, 0.4) !important;
}
</style>